/**
 * Order seed data - lifecycle coverage for the mock shopper: a completed
 * pickup (PKP-06), a shipment in transit (SHP-03), a staged pickup waiting in
 * store (PKP-02/PKP-04), a refunded group (PAY-05), and the auto-charged
 * auction-win order BF-10455 (AUC-07, D-05 - mirrors auction 1017).
 */
import { FulfilmentMethod, ItemCondition } from '@core/models/listing.enum';
import { OrderGroupStatus, PickupTicketStatus } from '@core/models/order.enum';
import { OrderState } from '@core/models/order.state';

import { daysAgo, daysFromNow, hoursAgo } from '../seed-time';

const PHOTO_JACKET = 'https://images.unsplash.com/photo-1611312449408-fcece27cdbb7?auto=format&fit=crop&w=800&q=70';
const PHOTO_TEAPOT = 'https://images.unsplash.com/photo-1765808608323-0d0dbf0f21ef?auto=format&fit=crop&w=800&q=70';
const PHOTO_AERON = 'https://images.unsplash.com/photo-1638720495078-facd4bd6625f?auto=format&fit=crop&w=800&q=70';
const PHOTO_CAMERA = 'https://images.unsplash.com/photo-1516961642265-531546e84af2?auto=format&fit=crop&w=800&q=70';
const PHOTO_MIRROR = 'https://images.unsplash.com/photo-1656797590428-653803a957fd?auto=format&fit=crop&w=800&q=70';

export const seedOrders = (): OrderState[] => [
  {
    id: 9001,
    orderRef: 'BF-10233',
    placedAt: daysAgo(18),
    email: 'jess@email.com',
    shopperId: 1,
    totals: { items: 42, shipping: 0, tax: 4.1, total: 46.1 },
    groups: [
      {
        id: 1,
        tenantId: 7,
        tenantName: 'Goodwill Industries of New Mexico',
        storeId: 79,
        storeName: 'Goodwill NM - San Mateo',
        storeAddressLine: '5000 San Mateo Blvd NE',
        fulfilment: FulfilmentMethod.Pickup,
        status: OrderGroupStatus.PickedUp,
        lines: [
          {
            id: 1,
            listingId: 999,
            title: 'Vintage Denim Chore Coat',
            condition: ItemCondition.Good,
            photoUrl: PHOTO_JACKET,
            unitPrice: 42
          }
        ],
        itemsSubtotal: 42,
        shippingAmount: 0,
        pickupTicket: {
          code: 'K4M-9Q1',
          qrPayload: 'BF-10233:K4M-9Q1',
          status: PickupTicketStatus.PickedUp,
          stagedAt: daysAgo(18),
          expiresAt: daysAgo(11),
          holdDays: 7
        },
        trackingNumber: null,
        etaLabel: null
      }
    ]
  },
  {
    id: 9002,
    orderRef: 'BF-10391',
    placedAt: daysAgo(6),
    email: 'jess@email.com',
    shopperId: 1,
    totals: { items: 36, shipping: 6.99, tax: 3.55, total: 46.54 },
    groups: [
      {
        id: 2,
        tenantId: 2,
        tenantName: 'Habitat ReStore LA',
        storeId: 21,
        storeName: 'Habitat ReStore LA - Pasadena',
        storeAddressLine: '770 N Fair Oaks Ave',
        fulfilment: FulfilmentMethod.Shipping,
        status: OrderGroupStatus.Shipped,
        lines: [
          {
            id: 2,
            listingId: 998,
            title: 'Mid-Century Ceramic Planter - Speckled',
            condition: ItemCondition.Excellent,
            photoUrl: PHOTO_TEAPOT,
            unitPrice: 36
          }
        ],
        itemsSubtotal: 36,
        shippingAmount: 6.99,
        pickupTicket: null,
        trackingNumber: '9400 1361 0930 0072 1450 88',
        etaLabel: 'Arrives Aug 10 – Aug 12'
      }
    ]
  },
  // The auction-win order: auto-charged at close of auction 1017 (D-05).
  // AuctionResult 1017 stamps this ref - the PDP won state links here.
  {
    id: 9003,
    orderRef: 'BF-10455',
    placedAt: hoursAgo(26),
    email: 'jess@email.com',
    shopperId: 1,
    totals: { items: 410, shipping: 0, tax: 38.95, total: 448.95 },
    groups: [
      {
        id: 3,
        tenantId: 7,
        tenantName: 'Goodwill Industries of New Mexico',
        storeId: 79,
        storeName: 'Goodwill NM - San Mateo',
        storeAddressLine: '5000 San Mateo Blvd NE',
        fulfilment: FulfilmentMethod.Pickup,
        status: OrderGroupStatus.Paid,
        lines: [
          {
            id: 1,
            listingId: 181,
            title: 'Herman Miller Aeron Chair - Size B',
            condition: ItemCondition.Good,
            photoUrl: PHOTO_AERON,
            unitPrice: 410
          }
        ],
        itemsSubtotal: 410,
        shippingAmount: 0,
        pickupTicket: {
          code: 'W7H-4NC',
          qrPayload: 'BF-10455:W7H-4NC',
          status: PickupTicketStatus.AwaitingStaging,
          stagedAt: null,
          expiresAt: daysFromNow(6),
          holdDays: 7
        },
        trackingNumber: null,
        etaLabel: null
      }
    ]
  },
  // Staged pickup waiting in store (PKP-02/PKP-04).
  {
    id: 9004,
    orderRef: 'BF-10412',
    placedAt: daysAgo(2),
    email: 'jess@email.com',
    shopperId: 1,
    totals: { items: 88, shipping: 0, tax: 8.36, total: 96.36 },
    groups: [
      {
        id: 4,
        tenantId: 7,
        tenantName: 'Goodwill Industries of New Mexico',
        storeId: 76,
        storeName: 'Goodwill NM - Lomas',
        storeAddressLine: '6001 Lomas Blvd NE, Suite R',
        fulfilment: FulfilmentMethod.Pickup,
        status: OrderGroupStatus.Staged,
        lines: [
          {
            id: 1,
            listingId: 996,
            title: 'Polaroid SX-70 Land Camera',
            condition: ItemCondition.Good,
            photoUrl: PHOTO_CAMERA,
            unitPrice: 88
          }
        ],
        itemsSubtotal: 88,
        shippingAmount: 0,
        pickupTicket: {
          code: 'R8T-2WK',
          qrPayload: 'BF-10412:R8T-2WK',
          status: PickupTicketStatus.Staged,
          stagedAt: hoursAgo(3),
          expiresAt: daysFromNow(4),
          holdDays: 7
        },
        trackingNumber: null,
        etaLabel: null
      }
    ]
  },
  // Refunded group - the shipment arrived damaged and was refunded (PAY-05).
  {
    id: 9005,
    orderRef: 'BF-10108',
    placedAt: daysAgo(33),
    email: 'jess@email.com',
    shopperId: 1,
    totals: { items: 48, shipping: 8.5, tax: 5.37, total: 61.87 },
    groups: [
      {
        id: 5,
        tenantId: 2,
        tenantName: 'Habitat ReStore LA',
        storeId: 21,
        storeName: 'Habitat ReStore LA - Pasadena',
        storeAddressLine: '770 N Fair Oaks Ave',
        fulfilment: FulfilmentMethod.Shipping,
        status: OrderGroupStatus.Refunded,
        lines: [
          {
            id: 1,
            listingId: 995,
            title: 'Art Deco Vanity Mirror - Beveled',
            condition: ItemCondition.Excellent,
            photoUrl: PHOTO_MIRROR,
            unitPrice: 48
          }
        ],
        itemsSubtotal: 48,
        shippingAmount: 8.5,
        pickupTicket: null,
        trackingNumber: null,
        etaLabel: null,
        refund: {
          amount: 61.87,
          destinationLabel: 'your Visa',
          refundedAt: daysAgo(30),
          reasonLabel: 'Item damaged in transit'
        }
      }
    ]
  }
];
